/**
 * THE FOUR BOOKS' OWN CHARTS.
 *
 * Owner decision, 2026-08-14: Visitors and Building Activities keep exactly the
 * charts they have; Parcels and Residents get bespoke ones. The visitor charts
 * cannot be reused for either — the line plots approved against non-approved
 * GUESTS and the pie slices by visitor type, and a parcel has no approval and no
 * headcount (the server sends `numberOfGuests: 0` for exactly that reason) while
 * every resident row is the same visitor type. Both would be a flat line at zero
 * and a single slice.
 *
 * ── ONE FIX TO THE VISITOR AGGREGATION ──
 *
 * The old bucketing counted a row into `approveGuests` when approval was 'Yes'
 * and into `nonApproveGuests` when `approval.length > 1` and not 'Yes' — so a
 * row with an EMPTY approval was counted in neither, while the "All Guests"
 * series was drawn as the SUM of the two. Every parcel and every daily-log row
 * with no approval was therefore invisible on a chart claiming to show all of
 * them. Here the total is the row total and the two decided series are drawn
 * against it, with the undecided remainder named rather than dropped.
 */
import React, { useMemo } from 'react';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  Pie,
  PieChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { dataViz, palette } from '../../../../../style-utils/theme';
import { ChartShell, ChartShellState } from '../charts/chart-shell';
import { ChartTable } from '../charts/chart-table-view';
import { DashLegend } from '../charts/chart-legend';
import { DashSimpleTooltip, DashTooltip } from '../charts/chart-tooltip';
import {
  ALERT_SAFE_SLOTS,
  CATEGORICAL_SLOTS,
  EMERGENCY_FILL,
  EMERGENCY_TYPE,
  PieChartData,
  UNRECORDED_EDGE,
  UNRECORDED_FILL,
  foldToCap,
} from '../charts/chart-palette';
import {
  ONE_BUCKET_NOTE,
  SYNC_ID,
  cursor,
  firstPaintAnimation,
  format,
  grid,
  lineMark,
  margins,
  oneBucket,
  oneBucketBar,
  soloPoint,
  xAxisTime,
  yAxisCount,
} from '../charts/chart-theme';
import { Book } from '../state/filter-model';
import { RecordRow } from '../data/records-api';

/* eslint-disable @typescript-eslint/no-explicit-any */
const num = (value: any): number => Number(value) || 0;
/* eslint-enable @typescript-eslint/no-explicit-any */

const byDay = <T extends { createdAt: string }>(
  rows: RecordRow[],
  seed: (day: string) => T,
  add: (bucket: T, row: RecordRow) => void
): T[] => {
  const map = new Map<string, T>();
  for (const row of rows) {
    const day = String(row.createdAt || '');
    if (!day) continue;
    if (!map.has(day)) map.set(day, seed(day));
    add(map.get(day) as T, row);
  }

  return Array.from(map.values()).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
};

export interface BookChartsProps {
  book: Book;
  rows: RecordRow[];
  /**
   * WHAT `rows` ACTUALLY IS — appended to every caveat on this panel group.
   *
   * These charts aggregate the rows the Records query returned, which is ONE
   * PAGE of fifty at offset `page * 50`. Every caveat below is written in the
   * language of the whole period — "how many arrived each day", "counted in
   * people" — and none of them said "from the fifty rows on this page". On a
   * busy building that is a chart of an arbitrary fifty records presented as the
   * month, and it moves when the reader pages.
   *
   * The panel cannot fix the scope — widening it means fetching up to five
   * thousand rows to draw a chart the analytics endpoints already answer
   * properly — so it states it. An unstated sample is the dishonest half.
   */
  scopeNote: string;
  /**
   * THE PERIOD-WIDE ANSWER, when the server could give one (QA-03).
   *
   * `rows` above is one page of fifty. These two are the SAME two questions
   * asked of `/analytics/batch` over the whole server-side filtered result, so
   * when they are present the two charts below stop being a chart of an
   * arbitrary fifty records presented as the month.
   *
   * Optional, and the page-scoped aggregation is kept as the fallback, because
   * the analytics endpoints are permission-gated and can be unavailable to a
   * caller who can still read the Records table. A degraded chart WITH its
   * caveat beats an empty panel; what is not acceptable is the degraded chart
   * WITHOUT the caveat, which is what this bug was.
   */
  serverDaily?: { day: string; all: number }[] | null;
  serverKinds?: PieChartData[] | null;
  /** How many people the server counted over the whole period, for the
   *  provenance line. Null when the period-wide answer is not available. */
  serverTotal?: number | null;
  state: ChartShellState;
  isMobile: boolean;
  seriesOff: string[];
  onToggleSeries: (panelId: string, key: string) => void;
  onExportPng: (panelId: string, svg: SVGSVGElement | null) => void;
}

export const BookCharts: React.FC<BookChartsProps> = ({
  book,
  rows,
  scopeNote,
  serverDaily,
  serverKinds,
  serverTotal,
  state,
  isMobile,
  seriesOff,
  onToggleSeries,
  onExportPng,
}) => {
  /* ── PARCELS: how many arrived per day, and how many of those are still on the
     shelf — the two facts the parcel desk is actually judged on. ── */
  const parcelSeries = useMemo(
    () =>
      book !== 'Parcel Log'
        ? []
        : byDay(
            rows,
            day => ({ createdAt: day, logged: 0, collected: 0, onShelf: 0 }),
            (bucket, row) => {
              const count = num(row.parcelCount) || 1;
              bucket.logged += count;
              if (row.status === 'picked_up') bucket.collected += count;
              else if (row.status !== 'returned') bucket.onShelf += count;
            }
          ),
    [book, rows]
  );

  /**
   * By carrier, which is how a desk recognises a parcel before it reads a name.
   *
   * Four named carriers by volume take slots 0-3; the rest fold. The 'Other'
   * value the desk can pick at intake is passed as `alwaysFold` — it already
   * means "a carrier I cannot name", which is the same thing the bucket means,
   * and letting it win a hue would put two slices called "Other" in one pie.
   *
   * 'Unrecorded' STAYS ITS OWN SLICE. It is not a small carrier: it means nobody
   * wrote the field down, which is a fact about the RECORD rather than about the
   * parcel, and folding it into "Other" would report a data-quality gap as a
   * minor courier.
   */
  const parcelPie = useMemo<PieChartData[]>(() => {
    if (book !== 'Parcel Log') return [];
    const totals = new Map<string, number>();
    let unrecorded = 0;
    let unnamed = 0;
    for (const row of rows) {
      const count = num(row.parcelCount) || 1;
      const carrier = String(row.carrier || '').trim();
      if (carrier === '') unrecorded += count;
      else if (carrier === 'Other') unnamed += count;
      else totals.set(carrier, (totals.get(carrier) || 0) + count);
    }
    // Alphabetical, so foldToCap has a stable declared order to paint from. Map
    // insertion order is ROW order, which changes with the filter window — it
    // would hand the same carrier a different hue in two reports.
    const named = Array.from(totals.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => a.name.localeCompare(b.name));
    const slices = foldToCap(
      named,
      CATEGORICAL_SLOTS,
      unnamed > 0 ? [{ name: 'Other', value: unnamed }] : []
    );

    return unrecorded > 0
      ? [
          ...slices,
          {
            name: 'Unrecorded',
            value: unrecorded,
            color: UNRECORDED_FILL,
            edge: UNRECORDED_EDGE,
          },
        ]
      : slices;
  }, [book, rows]);

  /* ── RESIDENTS: in against out, per day. A resident row is an arrival that may
     have acquired a departure, so "out" is counted off `departed_at` being
     present. Both series are plotted against the ARRIVAL's day, which is what
     this endpoint windows on. ── */
  const residentSeries = useMemo(
    () =>
      book !== 'Resident Log'
        ? []
        : byDay(
            rows,
            day => ({ createdAt: day, came: 0, left: 0, inside: 0 }),
            (bucket, row) => {
              bucket.came += 1;
              if (row.departed_at || row.departedAt) bucket.left += 1;
              else bucket.inside += 1;
            }
          ),
    [book, rows]
  );

  /* ── VISITORS AND BUILDING ACTIVITIES: people per day. ──
     NO "REFUSED" SERIES, AND THAT IS THE PRODUCT RATHER THAN THE CHART. The
     wizard asks an approval question and records the answer, but nothing in
     this application turns a "No" into a refusal: the entry is written, the
     person is logged, the desk carries on. A series called "Refused" therefore
     described an event that never happened, which is worse than an absent
     series — a reader counting refusals would have been counting answers. */
  const approvalSeries = useMemo(
    () =>
      book !== 'Visitor Log' && book !== 'Daily Log'
        ? []
        : byDay(
            rows,
            day => ({ createdAt: day, all: 0 }),
            (bucket, row) => {
              bucket.all += num(row.numberOfGuests) || 1;
            }
          ),
    [book, rows]
  );

  /* ── The kind mix, from the rows themselves. ── */
  const kindPie = useMemo<PieChartData[]>(() => {
    if (book === 'Parcel Log' || book === 'Resident Log') return [];
    const field = book === 'Daily Log' ? 'buildingActivity' : 'visitorType';
    const totals = new Map<string, number>();
    for (const row of rows) {
      const key = String(row[field] || '').trim() || '(not recorded)';
      totals.set(key, (totals.get(key) || 0) + (num(row.numberOfGuests) || 1));
    }

    if (book === 'Daily Log') {
      return foldToCap(
        Array.from(totals.entries()).map(([name, value]) => ({ name, value })),
        CATEGORICAL_SLOTS
      );
    }

    /* EMERGENCY IS A STATE, NOT A CATEGORY, and the state has to win. It is
       pinned to `palette.alert` BY NAME and taken out of the categorical
       rotation entirely — assigning it by slot index is how a 911 call ended up
       leaf-green, the colour the product uses for "checked in, all fine".
       The pinned slice costs a hue: coral sits ΔE 3.0 deutan from orange, so a
       chart that pins coral cannot also draw orange. Hence three slots, not
       four. */
    const emergency = totals.get(EMERGENCY_TYPE) || 0;
    totals.delete(EMERGENCY_TYPE);
    const rest = foldToCap(
      Array.from(totals.entries()).map(([name, value]) => ({ name, value })),
      ALERT_SAFE_SLOTS
    );

    return [
      ...rest,
      { name: EMERGENCY_TYPE, value: emergency, color: EMERGENCY_FILL },
    ];
  }, [book, rows]);

  const pieTotal = (slices: PieChartData[]) =>
    slices.reduce((sum, slice) => sum + slice.value, 0);

  const lineChart = (
    id: string,
    title: string,
    caveat: string,
    /* eslint-disable @typescript-eslint/no-explicit-any */
    series: any[],
    /* eslint-enable @typescript-eslint/no-explicit-any */
    keys: { key: string; label: string }[],
    emptyTitle: string,
    emptyText: string
  ) => (
    <ChartShell
      id={id}
      title={title}
      wide
      aspect={2.6}
      minHeight={260}
      state={state}
      caveat={caveat}
      extraCaveat={scopeNote || undefined}
      /* A one-day book still has to show its reading — see chart-theme's
         one-bucket section for why a line cannot. */
      notes={oneBucket(series.length) ? [ONE_BUCKET_NOTE] : undefined}
      emptyReason={
        !state.loading && !series.length ? { title: emptyTitle, text: emptyText } : null
      }
      summary={`${title}: ${series.length} buckets.`}
      onExportPng={svg => onExportPng(id, svg)}
      controls={
        <DashLegend
          panelId={id}
          entries={keys.map((k, i) => ({
            key: k.key,
            label: k.label,
            /* CONSECUTIVE SLOTS 0/1/2. These read 0/2/4 once, on the assumption
               that spreading out buys separation. It buys the opposite: the
               scale is validated ON THE ADJACENT SEQUENCE, so skipping slots
               steps outside the pairs that were measured. */
            tone: dataViz.series[i],
          }))}
          seriesOff={seriesOff}
          onToggle={key => onToggleSeries(id, key)}
        />
      }
      table={
        <ChartTable
          dimensionLabel="Day"
          valueLabel={keys[0].label}
          showCompare={false}
          rows={series.map(row => ({
            key: row.createdAt,
            label: format.dayKey(row.createdAt),
            value: keys.map(k => `${k.label} ${row[k.key]}`).join(' · '),
          }))}
        />
      }
    >
      <ComposedChart
        accessibilityLayer
        data={series}
        margin={margins.default}
        /* One category owns the whole plot width; without these the bars of a
           one-bucket book stand at opposite edges of the chart. */
        barCategoryGap={oneBucket(series.length) ? '38%' : undefined}
        barGap={oneBucket(series.length) ? 8 : undefined}
        syncId={SYNC_ID}
      >
        <CartesianGrid {...grid} />
        <XAxis dataKey="createdAt" tickFormatter={format.dayKey} {...xAxisTime} />
        <YAxis {...yAxisCount} />
        <Tooltip
          content={
            <DashTooltip
              series={keys.map((k, i) => ({
                key: k.key,
                label: k.label,
                tone: dataViz.series[i],
              }))}
              labelOf={raw => format.dayKey(String(raw))}
            />
          }
          cursor={cursor.line}
        />
        {keys.map((k, i) =>
          seriesOff.includes(`${id}:${k.key}`) ? null : oneBucket(series.length) ? (
            <Bar
              key={k.key}
              dataKey={k.key}
              name={k.label}
              fill={dataViz.series[i]}
              {...oneBucketBar}
            />
          ) : (
            <Line
              key={k.key}
              dataKey={k.key}
              name={k.label}
              {...lineMark}
              {...soloPoint(series.length, dataViz.series[i])}
              stroke={dataViz.series[i]}
              {...firstPaintAnimation(`${id}-${k.key}`)}
            />
          )
        )}
      </ComposedChart>
    </ChartShell>
  );

  const pieChart = (id: string, title: string, caveat: string, slices: PieChartData[]) => (
    <ChartShell
      id={id}
      title={title}
      /* FULL WIDTH, like every other panel on this tab. A donut is the only
         non-wide shape here, so at two columns it sat alone in the left half
         with an empty right half beside it. The ring is limited by the SHORTER
         side, so a wider box does not inflate it — it just stops the panel
         being the one thing on the screen that is half a screen. */
      wide
      aspect={2.6}
      minHeight={280}
      state={state}
      caveat={caveat}
      extraCaveat={scopeNote || undefined}
      emptyReason={
        !state.loading && !slices.length
          ? {
              title: 'Nothing to break down',
              text: 'No row in this period carried the field this panel splits on.',
            }
          : null
      }
      summary={`${title}: ${slices
        .map(s => `${s.name} ${s.value.toLocaleString()}`)
        .join(', ')}.`}
      onExportPng={svg => onExportPng(id, svg)}
      table={
        <ChartTable
          dimensionLabel="Kind"
          valueLabel="Count"
          showCompare={false}
          rows={slices.map(slice => ({
            key: slice.name,
            label: slice.name,
            value: slice.value.toLocaleString(),
          }))}
        />
      }
    >
      <PieChart margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
        <Tooltip
          content={
            <DashSimpleTooltip
              tone={palette.iris}
              valueKey="value"
              labelKey="name"
              total={pieTotal(slices)}
            />
          }
        />
        <Pie
          data={slices}
          dataKey="value"
          nameKey="name"
          innerRadius="52%"
          outerRadius="78%"
          paddingAngle={1}
          stroke={palette.surface}
          strokeWidth={2}
          label={!isMobile}
          labelLine={!isMobile}
          {...firstPaintAnimation(id)}
        >
          {slices.map((slice, i) => (
            <Cell
              key={`${slice.name}-${i}`}
              fill={slice.color}
              /* Only "Unrecorded" sets `edge`, and it is what makes that
                 near-white wedge a shape instead of a gap. */
              stroke={slice.edge || palette.surface}
              strokeWidth={2}
            />
          ))}
        </Pie>
      </PieChart>
    </ChartShell>
  );

  if (book === 'Parcel Log') {
    return (
      <>
        {lineChart(
          'parcels-flow',
          'Parcels in, collected and waiting',
          'How many arrived each day, how many of those were collected, and how many are still on the shelf.',
          parcelSeries,
          [
            { key: 'logged', label: 'Taken in' },
            { key: 'collected', label: 'Collected' },
            { key: 'onShelf', label: 'Still on the shelf' },
          ],
          'No parcels in this period',
          'Nothing was taken in under these filters. A quiet week and an empty shelf are different things.'
        )}
        {pieChart(
          'parcels-carrier',
          'Parcels by carrier',
          'The four largest carriers are named; the rest fold into one bucket. "Unrecorded" is its own slice — it means nobody wrote the field down, which is a fact about the record rather than about the parcel.',
          parcelPie
        )}
      </>
    );
  }

  if (book === 'Resident Log') {
    return lineChart(
      'residents-flow',
      'Residents in and out',
      'Both series are plotted against the ARRIVAL’s day, which is what this query windows on — a departure on a later day is counted on the day the visit started.',
      residentSeries,
      [
        { key: 'came', label: 'Came in' },
        { key: 'left', label: 'Signed out' },
        { key: 'inside', label: 'No departure recorded' },
      ],
      'No resident movements in this period',
      'Nothing was logged under these filters.'
    );
  }

  /* WHICH ANSWER IS BEING DRAWN, AND SAYING SO (QA-03).
   *
   * The server-side series is preferred whenever it exists — including when it
   * is EMPTY, which is a real answer ("nothing in this period") and not a
   * missing one. `null` means the request failed or was not made, and only then
   * does the page-scoped aggregation run, carrying `scopeNote`.
   *
   * The provenance line is not decoration. A number this screen is read from to
   * make staffing decisions has to carry where it came from, or the next reader
   * cannot tell a period-wide figure from a fifty-row sample — which is exactly
   * the confusion that made this a bug rather than a caption. */
  const usingServer = Array.isArray(serverDaily);
  const dailyData = usingServer
    ? (serverDaily as { day: string; all: number }[]).map(d => ({
        createdAt: d.day,
        all: d.all,
      }))
    : approvalSeries;
  const kindData = Array.isArray(serverKinds) ? serverKinds : kindPie;

  /* WHAT THE UNIT IS, per book, said rather than assumed.
   *
   * The visitor log is measured in PEOPLE (`people_admitted`) — a party of four
   * is four. The daily log cannot be: `people` is a visitor_log predicate and
   * returns zero for building activities, so that book is measured in RECORDS.
   * Printing "people" over a figure that counts entries would be the same class
   * of untruth as printing a page-scoped sample under a period title. */
  const unit = book === 'Visitor Log' ? 'people' : 'entries';
  const provenance = usingServer
    ? `Counted over the whole selected period and every applied filter${
        typeof serverTotal === 'number'
          ? ` — ${serverTotal.toLocaleString()} ${unit} in total`
          : ''
      }.`
    : scopeNote;

  return (
    <>
      {lineChart(
        'approvals',
        'People logged each day',
        `${
          book === 'Visitor Log'
            ? 'Counted in PEOPLE, not records — a party of four is four. Every logged person is here: nothing in this application refuses anybody, so there is no decision to split this line by.'
            : 'Counted in RECORDS — one logged building activity is one. There is no headcount on a building activity to count instead.'
        } ${provenance}`.trim(),
        dailyData,
        [{ key: 'all', label: 'Everybody' }],
        'Nothing to plot',
        'No row in this period falls in this window.'
      )}
      {pieChart(
        'book-kinds',
        book === 'Daily Log' ? 'Building activities by kind' : 'Types of visitors',
        `${
          book === 'Daily Log'
            ? 'Counted in records. The four largest are named; the rest fold into one bucket.'
            : 'Counted in people. Emergency (911) is pinned to the alert colour by name and taken out of the categorical rotation — a 911 call must never render in the colour the product uses for "checked in, all fine".'
        } ${provenance}`.trim(),
        kindData
      )}
    </>
  );
};
